import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { motion, AnimatePresence } from 'motion/react';
import { X, ZoomIn, ZoomOut, Check, Scissors, Image as ImageIcon, Play, Pause, Sparkles, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { toast } from 'sonner';

interface MediaTrimmerModalProps {
    isOpen: boolean;
    onClose: () => void;
    mediaUrl: string;
    mediaBlob?: Blob;
    mediaType: 'image' | 'video';
    onSave: (data: { 
        photoUrl: string; 
        videoBlob?: Blob;
        pickedStillUrl?: string; // Original uncropped still from video
        trimStart?: number;
        trimEnd?: number;
    }) => void;
    aspectRatio?: number; // width / height (usually 3/4 or 9/16 for match pane)
    title?: string;
    circular?: boolean;
    /** Max trim length in seconds. Portfolio clips default to 5; Yap videos pass 180. */
    maxDurationSec?: number;
    /** Video-only flow: after trimming, save immediately and skip the still-pick / crop steps. */
    videoOnly?: boolean;
}

export function MediaTrimmerModal({
    isOpen,
    onClose,
    mediaUrl,
    mediaBlob,
    mediaType,
    onSave,
    aspectRatio = 1,
    title = "Trim & Crop",
    circular = false,
    maxDurationSec = 3600,
    videoOnly = false,
}: MediaTrimmerModalProps) {
    const { theme } = useTheme();
    
    // Common State
    const [step, setStep] = useState<'trim' | 'pick' | 'crop'>(mediaType === 'video' ? 'trim' : 'crop');
    const [loading, setLoading] = useState(false);

    // Image/Hero Frame Cropper State
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [dimensions, setDimensions] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentImageUrl, setCurrentImageUrl] = useState(mediaUrl);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [duration, setDuration] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoHasRendered, setVideoHasRendered] = useState(false);
    const MAX_DURATION = maxDurationSec;

    // Reset state when media changes or modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(mediaType === 'video' ? 'trim' : 'crop');
            setCurrentImageUrl(mediaUrl);
            setScale(1);
            setPosition({ x: 0, y: 0 });
            setStartTime(0);
            // Leave end at 0 until the real metadata loads (onVideoMetadata sets
            // it to min(actualDuration, max)). Avoids showing a wrong cap value.
            setEndTime(0);
            setCurrentTime(0);
            setDuration(0);
        }
        // NOTE: intentionally NOT depending on `duration` — onVideoMetadata sets
        // both duration and endTime once the real metadata loads, and re-running
        // this on that change would wipe endTime back to 0.
    }, [isOpen, mediaUrl, mediaType]);

    // Initialize Image Dimensions
    useEffect(() => {
        if (isOpen && currentImageUrl && (step === 'crop' || step === 'pick')) {
            const img = new Image();
            img.onload = () => {
                if (containerRef.current) {
                    const containerWidth = containerRef.current.offsetWidth;
                    const containerHeight = containerRef.current.offsetHeight;
                    const imgRatio = img.naturalWidth / img.naturalHeight;
                    const containerRatio = containerWidth / containerHeight;

                    let renderWidth, renderHeight;
                    if (imgRatio > containerRatio) {
                        renderHeight = containerHeight;
                        renderWidth = containerHeight * imgRatio;
                    } else {
                        renderWidth = containerWidth;
                        renderHeight = containerWidth / imgRatio;
                    }

                    setDimensions({
                        width: renderWidth,
                        height: renderHeight,
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight
                    });
                }
            };
            img.src = currentImageUrl;
        }
    }, [isOpen, currentImageUrl, step]);

    // Handle Video Metadata
    const onVideoMetadata = () => {
        if (videoRef.current) {
            const d = videoRef.current.duration;
            if (!isFinite(d) || d <= 0) return;
            setDuration(d);
            // Default the selection to the whole clip, capped at the max. Always
            // recompute from the REAL duration (don't gate on endTime===0, which
            // left a stale 180s value for shorter clips).
            setStartTime(0);
            setEndTime(Math.min(d, MAX_DURATION));
            
            // 🔒 FIX: Seek slightly to force a frame render, then explicitly call play to bypass mobile stalls
            videoRef.current.currentTime = 0.001;
            if (step === 'trim' && isPlaying) {
                videoRef.current.play().catch(err => console.warn('[Trimmer] AutoPlay stalled:', err));
            }
            setVideoHasRendered(true);
        }
    };

    // Format seconds as m:ss for the trim timeline.
    const fmtClock = (s: number) => {
        const total = Math.max(0, Math.floor(s || 0));
        const m = Math.floor(total / 60);
        const sec = total % 60;
        return `${m}:${String(sec).padStart(2, '0')}`;
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) videoRef.current.pause();
            else videoRef.current.play().catch(err => console.warn('[Trimmer] play toggle stalled:', err));
            setIsPlaying(!isPlaying);
        }
    };

    const handleTrimConfirm = () => {
        const trimDuration = endTime - startTime;
        if (trimDuration <= 0) {
            toast.error('Please select a valid video segment');
            return;
        }
        if (trimDuration > MAX_DURATION + 0.1) {
            toast.error(`Maximum allowed duration is ${MAX_DURATION} seconds`);
            return;
        }
        // Video-only flow (e.g. Yap videos): no still/crop step — save the trim now.
        if (videoOnly) {
            handleVideoOnlySave();
            return;
        }
        setStep('pick');
        if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
            videoRef.current.currentTime = startTime;
            setCurrentTime(startTime);
        }
    };

    // Save just the trimmed video (+ a poster frame) without the portfolio
    // still-pick/crop steps. Playback is clamped to [startTime, endTime].
    const handleVideoOnlySave = async () => {
        setLoading(true);
        try {
            let photoUrl = currentImageUrl;
            if (videoRef.current && videoRef.current.videoWidth) {
                const c = document.createElement('canvas');
                c.width = videoRef.current.videoWidth;
                c.height = videoRef.current.videoHeight;
                const cx = c.getContext('2d');
                if (cx) { cx.drawImage(videoRef.current, 0, 0, c.width, c.height); photoUrl = c.toDataURL('image/jpeg', 0.9); }
            }
            let videoBlobToPass: Blob | undefined;
            if (mediaBlob) videoBlobToPass = mediaBlob;
            else { const r = await fetch(mediaUrl); videoBlobToPass = await r.blob(); }
            await onSave({ photoUrl, videoBlob: videoBlobToPass, pickedStillUrl: photoUrl, trimStart: startTime, trimEnd: endTime });
        } catch (err) {
            console.error('[MediaTrimmer] video-only save failed:', err);
            toast.error('Failed to process video. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const captureHeroFrame = async () => {
        if (!videoRef.current) return;
        
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            setCurrentImageUrl(canvas.toDataURL('image/jpeg', 0.95));
            setStep('crop');
        }
    };

    const handleSave = async () => {
        if (!containerRef.current) return;
        setLoading(true);

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const outputWidth = 600;
            const outputHeight = outputWidth / aspectRatio;
            canvas.width = outputWidth;
            canvas.height = outputHeight;

            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = currentImageUrl;
            });

            const containerWidth = containerRef.current.offsetWidth;
            const containerHeight = containerRef.current.offsetHeight;
            const finalRenderWidth = dimensions.width * scale;
            const finalRenderHeight = dimensions.height * scale;
            const drawX = (containerWidth - finalRenderWidth) / 2 + position.x;
            const drawY = (containerHeight - finalRenderHeight) / 2 + position.y;
            const scaleFactor = outputWidth / containerWidth;

            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, outputWidth, outputHeight);
            
            ctx.save();
            if (circular) {
                ctx.beginPath();
                ctx.arc(outputWidth / 2, outputHeight / 2, outputWidth / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
            }

            ctx.drawImage(img, drawX * scaleFactor, drawY * scaleFactor, finalRenderWidth * scaleFactor, finalRenderHeight * scaleFactor);
            ctx.restore();

            const photoUrl = canvas.toDataURL('image/jpeg', 0.95);
            console.log('[MediaTrimmer] Saving media...', { mediaType, step, aspectRatio });
            
            // If it's a video step, we pass back the full uncropped still Frame 
            // so step 2 (Square) can crop it differently.
            let videoBlobToPass: Blob | undefined = undefined;
            if (mediaType === 'video') {
                try {
                    let finalVideoBlob: Blob;
                    
                    if (mediaBlob) {
                        console.log('[MediaTrimmer] Using direct mediaBlob from props');
                        finalVideoBlob = mediaBlob;
                    } else {
                        console.log('[MediaTrimmer] No blob provided, fetching from:', mediaUrl);
                        const response = await fetch(mediaUrl);
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                        finalVideoBlob = await response.blob();
                    }

                    console.log('[MediaTrimmer] Video blob captured successfully', {
                        size: finalVideoBlob.size,
                        type: finalVideoBlob.type
                    });
                    
                    videoBlobToPass = finalVideoBlob;
                } catch (err) {
                    console.error('[MediaTrimmer] Failed to acquire video blob:', err);
                    toast.error('Failed to process video data. Please try again.');
                    setLoading(false);
                    return;
                }
            }

            await onSave({ 
                photoUrl, 
                videoBlob: videoBlobToPass,
                pickedStillUrl: mediaType === 'video' ? currentImageUrl : undefined,
                trimStart: mediaType === 'video' ? startTime : undefined,
                trimEnd: mediaType === 'video' ? endTime : undefined
            });
            console.log('[MediaTrimmer] onSave completed');
        } catch (error: any) {
            console.error('[MediaTrimmer] Error saving media:', error);
            toast.error(`Error saving media: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    // Drag Logic (Universal Touch + Mouse)
    const handleDragStart = (clientX: number, clientY: number) => {
        setIsDragging(true);
        setDragStart({ x: clientX - position.x, y: clientY - position.y });
    };

    const handleDragMove = (clientX: number, clientY: number) => {
        if (!isDragging || !containerRef.current) return;
        
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const scaledWidth = dimensions.width * scale;
        const scaledHeight = dimensions.height * scale;

        // Calculate limits: image must cover the entire container (no black bars)
        const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
        const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

        const newX = clientX - dragStart.x;
        const newY = clientY - dragStart.y;

        setPosition({
            x: Math.max(-maxX, Math.min(newX, maxX)),
            y: Math.max(-maxY, Math.min(newY, maxY))
        });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        handleDragStart(e.clientX, e.clientY);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        handleDragMove(e.clientX, e.clientY);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    if (!isOpen) return null;

    const renderHeader = () => (
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
            <h2 className="text-xl font-bold text-black dark:text-white flex items-center gap-2">
                {step === 'trim' && <Scissors className="w-5 h-5 text-blue-500 dark:text-blue-400" />}
                {step === 'pick' && <ImageIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />}
                {step === 'crop' && <Sparkles className="w-5 h-5 text-blue-500 dark:text-blue-400" />}
                {step === 'pick' ? 'Pick Still Image' : title}
            </h2>
            <button onClick={onClose} className="text-[#666666] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white p-2">
                <X className="w-6 h-6" />
            </button>
        </div>
    );

    const modalContent = (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 10000 }}>
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-lg bg-white dark:bg-[#1a1a1a] rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
                style={{ zIndex: 10001 }}
            >
                {renderHeader()}

                <div className="p-6 overflow-y-auto flex-1 min-h-0 flex flex-col items-center">
                    {/* VIDEO TRIMMER SCREEN */}
                    {step === 'trim' && (
                        <div className="w-full flex flex-col items-center gap-6">
                            <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-inner group">
                                <video 
                                    ref={videoRef} 
                                    src={mediaUrl} 
                                    onLoadedMetadata={onVideoMetadata}
                                    onTimeUpdate={(e) => {
                                        const vid = e.currentTarget;
                                        if (endTime > 0 && vid.currentTime >= endTime) {
                                            vid.currentTime = startTime;
                                            if (isPlaying) vid.play();
                                        }
                                        setCurrentTime(vid.currentTime);
                                    }}
                                    className="w-full h-full object-contain"
                                    onClick={togglePlay}
                                    playsInline
                                    muted={false}
                                    preload="auto"
                                    autoPlay
                                />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={togglePlay} className="p-4 bg-black/50 rounded-full text-white pointer-events-auto backdrop-blur-sm">
                                        {isPlaying ? <Pause /> : <Play />}
                                    </button>
                                </div>
                            </div>

                            <div className="w-full space-y-4">
                                <div className="flex justify-between text-xs text-secondary font-mono">
                                    <span>{fmtClock(startTime)}</span>
                                    <span className="text-blue-500 font-bold">Selected: {(endTime - startTime).toFixed(1)}s</span>
                                    <span>{fmtClock(endTime)}</span>
                                </div>
                                <Slider
                                    value={[startTime, endTime]}
                                    min={0}
                                    max={duration || maxDurationSec}
                                    step={0.1}
                                    minStepsBetweenThumbs={1}
                                    onValueChange={(v) => {
                                        const newStart = v[0];
                                        const newEnd = v[1];
                                        
                                        // Enforce MAX_DURATION
                                        if (newEnd - newStart > MAX_DURATION) {
                                            if (newStart !== startTime) {
                                                // Start moved, shift end to match MAX_DURATION
                                                setStartTime(newStart);
                                                setEndTime(Math.min(newStart + MAX_DURATION, duration));
                                            } else {
                                                // End moved, shift start to match MAX_DURATION
                                                setEndTime(newEnd);
                                                setStartTime(Math.max(0, newEnd - MAX_DURATION));
                                            }
                                        } else {
                                            setStartTime(newStart);
                                            setEndTime(newEnd);
                                        }

                                        // No real-time scrubbing — setting the handles is
                                        // cheap; the user verifies on the trimmed preview after
                                        // processing. (Avoids per-frame seeks that lag the UI.)
                                    }}
                                />
                                <p className="text-center text-xs text-muted-foreground italic">
                                    Drag the handles to trim{duration > 0 ? ` (clip is ${fmtClock(duration)})` : ''}. Max {MAX_DURATION >= 60 ? `${Math.round(MAX_DURATION / 60)} min` : `${MAX_DURATION}s`}.
                                </p>
                            </div>

                            <button
                                onClick={handleTrimConfirm}
                                disabled={endTime - startTime <= 0 || loading}
                                className="w-full h-12 px-6 bg-linear-to-r from-[#4478ea] to-[#2d1ef1] hover:opacity-90 font-bold rounded-xl shadow-lg border-0 flex items-center justify-center text-white! disabled:opacity-50"
                                style={{ color: 'white' }}
                            >
                                <span>{videoOnly ? (loading ? 'Processing video…' : 'Done') : 'Next: Select Still Image'}</span>
                                {!videoOnly && <ChevronRight className="ml-2 w-4 h-4 text-white!" />}
                            </button>
                        </div>
                    )}

                    {/* STILL PICKER SCREEN */}
                    {step === 'pick' && (
                        <div className="w-full flex flex-col items-center gap-6">
                            <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-inner">
                                <video 
                                    ref={videoRef} 
                                    src={mediaUrl} 
                                    className="w-full h-full object-contain"
                                    playsInline
                                    muted
                                    preload="auto"
                                />
                            </div>
                            
                            <div className="w-full space-y-4">
                                <div className="flex justify-between text-xs text-secondary font-mono">
                                    <span>{startTime.toFixed(1)}s</span>
                                    <span>Current: {currentTime.toFixed(1)}s</span>
                                    <span>{(startTime + MAX_DURATION).toFixed(1)}s</span>
                                </div>
                                <Slider 
                                    value={[currentTime]} 
                                    min={startTime} 
                                    max={startTime + MAX_DURATION} 
                                    step={0.05} 
                                    onValueChange={(v) => {
                                        setCurrentTime(v[0]);
                                        if (videoRef.current) videoRef.current.currentTime = v[0];
                                    }}
                                />
                                <p className="text-center text-xs text-muted-foreground italic">
                                    Scrub to find the perfect frame for your profile's static image.
                                </p>
                            </div>

                            <div className="flex gap-4 w-full">
                                <button 
                                    onClick={() => setStep('trim')} 
                                    className="flex-1 h-12 border-2 border-black/20 dark:border-white/20 rounded-xl flex items-center justify-center font-bold text-black dark:text-white"
                                >
                                    <ChevronLeft className="mr-2 w-4 h-4" /> Back
                                </button>
                                <button 
                                    onClick={captureHeroFrame} 
                                    className="flex-2 shrink-0 h-12 px-6 bg-linear-to-r from-[#4478ea] to-[#2d1ef1] hover:opacity-90 font-bold rounded-xl shadow-lg border-0 flex items-center justify-center text-white!"
                                    style={{ color: 'white' }}
                                >
                                    <span>Select Frame</span> <Check className="ml-2 w-4 h-4 text-white!" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* CROPPER SCREEN */}
                    {step === 'crop' && (
                        <div className="w-full flex flex-col items-center gap-6">
                            <div 
                                ref={containerRef}
                                className={`relative bg-black/20 dark:bg-white/5 overflow-hidden border-4 ${circular ? 'rounded-full' : 'rounded-3xl shadow-xl'}`}
                                style={{
                                    width: '100%',
                                    maxWidth: '320px',
                                    aspectRatio: `${aspectRatio}`,
                                    borderColor: '#3B82F6'
                                }}
                                onMouseMove={handleMouseMove}
                                onMouseUp={() => setIsDragging(false)}
                                onMouseLeave={() => setIsDragging(false)}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={() => setIsDragging(false)}
                                onTouchCancel={() => setIsDragging(false)}
                            >
                                {mediaType === 'video' ? (
                                    <video 
                                        key={`crop-video-${startTime}`}
                                        src={mediaUrl}
                                        muted
                                        loop
                                        playsInline
                                        onLoadedMetadata={(e) => {
                                            // 🔒 Step 1: Seek to trimStart BEFORE any frames can play.
                                            // This fires early enough on iOS/Safari that frame 0 is never shown.
                                            e.currentTarget.currentTime = startTime;
                                        }}
                                        onSeeked={(e) => {
                                            // 🔒 Step 2: Play only AFTER the seek has confirmed landing.
                                            e.currentTarget.play().catch(() => {});
                                        }}
                                        onTimeUpdate={(e) => {
                                            // 🔒 Step 3: Loop boundary enforcement.
                                            const vid = e.currentTarget;
                                            if (vid.currentTime >= endTime || vid.currentTime < startTime - 0.2) {
                                                vid.currentTime = startTime;
                                            }
                                        }}
                                        style={{
                                            left: '50%',
                                            top: '50%',
                                            transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                            width: dimensions.width > 0 ? `${dimensions.width}px` : '100%',
                                            height: dimensions.height > 0 ? `${dimensions.height}px` : '100%',
                                            transformOrigin: 'center center',
                                        }}
                                        onMouseDown={handleMouseDown}
                                        onTouchStart={handleTouchStart}
                                        className="absolute max-w-none cursor-move select-none"
                                    />
                                ) : (
                                    <motion.img 
                                        src={currentImageUrl}
                                        style={{
                                            left: '50%',
                                            top: '50%',
                                            transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                            width: dimensions.width > 0 ? `${dimensions.width}px` : '100%',
                                            height: dimensions.height > 0 ? `${dimensions.height}px` : '100%',
                                            transformOrigin: 'center center',
                                        }}
                                        onMouseDown={handleMouseDown}
                                        onTouchStart={handleTouchStart}
                                        className="absolute max-w-none cursor-move select-none"
                                        draggable={false}
                                    />
                                )}
                                <div className="absolute inset-0 border border-white/20 pointer-events-none">
                                    <div className="h-full w-1/3 border-r border-white/10 absolute left-1/3" />
                                    <div className="h-full w-1/3 border-l border-white/10 absolute right-1/3" />
                                    <div className="w-full h-1/3 border-b border-white/10 absolute top-1/3" />
                                    <div className="w-full h-1/3 border-t border-white/10 absolute bottom-1/3" />
                                </div>
                            </div>

                            <div className="w-full space-y-4">
                                <div className="flex items-center gap-3">
                                    <ZoomOut className="w-5 h-5 text-muted-foreground" />
                                    <Slider value={[scale]} min={1} max={3} step={0.1} onValueChange={(v) => setScale(v[0])} className="flex-1" />
                                    <ZoomIn className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <p className="text-center text-xs text-muted-foreground italic">
                                     Adjust the focus for your {aspectRatio === 1 ? 'Profile' : 'Match Pane'} display.
                                </p>
                            </div>

                            <div className="flex gap-4 w-full">
                                {mediaType === 'video' && (
                                <button 
                                    onClick={() => setStep('pick')} 
                                    className="h-12 w-12 border-2 border-black/20 dark:border-white/20 rounded-xl flex items-center justify-center font-bold text-black dark:text-white"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                )}
                                <button 
                                    onClick={handleSave} 
                                    disabled={loading}
                                    className="flex-1 h-12 px-6 bg-linear-to-r from-[#4478ea] to-[#2d1ef1] hover:opacity-90 font-bold rounded-xl shadow-lg border-0 flex items-center justify-center text-white! disabled:opacity-50"
                                    style={{ color: 'white' }}
                                >
                                    <span>{loading ? 'Processing...' : 'Confirm Media'}</span> <Check className="ml-2 w-4 h-4 text-white!" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
